import { Layout } from '@/components/Layout';
import * as Sentry from "@sentry/react";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { VersionGuard } from "@/hooks/useVersionCheck";
import { isNonRetryable } from "@/lib/queryErrors";
import { TenantProvider } from "@/providers/TenantProvider";
import { RealtimeProvider } from "@/providers/RealtimeProvider";
import { SoftphoneProvider } from "@/components/phone/SoftphoneProvider";
import { TimezoneProvider } from "@/contexts/TimezoneContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import InviteAccept from "./pages/InviteAccept";
import CompanyInviteAccept from "./pages/CompanyInviteAccept";
import CompanySetup from "./pages/CompanySetup";
import DownloadApp from "./pages/DownloadApp";
import SystemHealth from "./pages/SystemHealth";
import Dashboard from "./pages/Dashboard";
import Leads from "./pages/Leads";
import CreateLead from "./pages/CreateLead";
import LeadDetail from "./pages/LeadDetail";
import Jobs from "./pages/Jobs";
import Schedule from "./pages/Schedule";
import LiveMap from "./pages/LiveMap";
import JobDetail from "./pages/JobDetail";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import Reports from "./pages/Reports";
import Invoices from "./pages/Invoices";
import Estimates from "./pages/Estimates";
import EstimateSign from "./pages/EstimateSign";
import ReviewStars from "./pages/ReviewStars";
import ReviewUnsubscribe from "./pages/ReviewUnsubscribe";
import PortalIndex from "./portal/PortalIndex";
import PortalRoutes from "./portal/PortalRoutes";
import Communications from "./pages/Communications";
import Settings from "./pages/Settings";
import Profile from "./pages/Profile";
import Users from "./pages/Users";
import SuperAdmin from "./pages/settings/SuperAdmin";
import AdminUtilities from "./pages/AdminUtilities";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";

import NotFound from "./pages/NotFound";
import DebugRevenue from "./pages/DebugRevenue";
import GoogleAdsCallback from "./pages/GoogleAdsCallback";
import SquareCallback from "./pages/SquareCallback";
import XeroCallback from "./pages/XeroCallback";

/** Schedule and Map moved under Jobs; old links and bookmarks keep their day and view. */
function MovedTo({ path }: { path: string }) {
  const { search } = useLocation();
  return <Navigate to={path + search} replace />;
}

// Wraps raw Supabase PostgrestError objects into proper JS Errors
// so Sentry receives a real stack + code/hint/details as context
function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object' && 'message' in err) {
    const pgErr = err as { message: string; code?: string; details?: string; hint?: string };
    const e = new Error(`[DB ${pgErr.code ?? 'error'}] ${pgErr.message}`);
    e.name = 'PostgrestError';
    (e as any).details = pgErr.details;
    (e as any).hint = pgErr.hint;
    (e as any).code = pgErr.code;
    return e;
  }
  return new Error(String(err));
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchIntervalInBackground: false,
      retry: (failureCount, error) => !isNonRetryable(error) && failureCount < 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15_000),
    },
    mutations: {
      retry: 0,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      Sentry.captureException(toError(error), {
        tags: { layer: "query" },
        extra: { queryKey: JSON.stringify(query.queryKey) },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      Sentry.captureException(toError(error), {
        tags: { layer: "mutation" },
        extra: { mutationKey: JSON.stringify(mutation.options.mutationKey) },
      });
    },
  }),
});

const App = () => (
  <AppErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <VersionGuard />
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <TenantProvider>
          <TimezoneProvider>
            <RealtimeProvider>
              <SoftphoneProvider>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/invite/accept/:token" element={<InviteAccept />} />
                <Route path="/company-invite/accept" element={<CompanyInviteAccept />} />
                <Route path="/sign/:token" element={<EstimateSign />} />
                <Route path="/r/:token" element={<ReviewStars />} />
                <Route path="/r/:token/unsubscribe" element={<ReviewUnsubscribe />} />
                {/* Customer portal: its own login (magic link), guarded inside PortalRoutes */}
                <Route path="/portal" element={<PortalIndex />} />
                <Route path="/portal/:slug/*" element={<PortalRoutes />} />
                <Route path="/company-setup" element={<ProtectedRoute><CompanySetup /></ProtectedRoute>} />
                <Route path="/download-app" element={<ProtectedRoute><DownloadApp /></ProtectedRoute>} />
                <Route path="/system-health" element={<ProtectedRoute requireAdmin><SystemHealth /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/leads" element={<ProtectedRoute><Leads /></ProtectedRoute>} />
                <Route path="/leads/new" element={<ProtectedRoute><CreateLead /></ProtectedRoute>} />
                <Route path="/leads/:id" element={<ProtectedRoute><LeadDetail /></ProtectedRoute>} />
                <Route path="/jobs" element={<ProtectedRoute><Jobs /></ProtectedRoute>} />
                <Route path="/jobs/:id" element={<ProtectedRoute><JobDetail /></ProtectedRoute>} />
                <Route path="/jobs/schedule" element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
                <Route path="/jobs/map" element={<ProtectedRoute><LiveMap /></ProtectedRoute>} />
                <Route path="/schedule" element={<MovedTo path="/jobs/schedule" />} />
                <Route path="/map" element={<MovedTo path="/jobs/map" />} />
                <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
                <Route path="/customers/:id" element={<ProtectedRoute><CustomerDetail /></ProtectedRoute>} />
                <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
                <Route path="/invoices/:id" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
                <Route path="/estimates" element={<ProtectedRoute><Estimates /></ProtectedRoute>} />
                <Route path="/estimates/:id" element={<ProtectedRoute><Estimates /></ProtectedRoute>} />
                <Route path="/communications" element={<ProtectedRoute><Communications /></ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="/settings/super-admin" element={<ProtectedRoute><Layout><SuperAdmin /></Layout></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/users" element={<ProtectedRoute requireAdmin><Users /></ProtectedRoute>} />
                <Route path="/admin-utilities" element={<ProtectedRoute requireAdmin><AdminUtilities /></ProtectedRoute>} />
                <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
                <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />

                <Route path="/debug-revenue" element={<ProtectedRoute requireAdmin><DebugRevenue /></ProtectedRoute>} />
                <Route path="/auth/google-ads/callback" element={<ProtectedRoute><GoogleAdsCallback /></ProtectedRoute>} />
                <Route path="/auth/square/callback" element={<ProtectedRoute><SquareCallback /></ProtectedRoute>} />
                <Route path="/auth/xero/callback" element={<ProtectedRoute><XeroCallback /></ProtectedRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              </SoftphoneProvider>
            </RealtimeProvider>
          </TimezoneProvider>
          </TenantProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </AppErrorBoundary>
);

export default App;
